import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import api, { errorMessage } from '../../services/api';
import { useAuth } from '../../services/auth';
import { PageNav, PageAlert, Modal, inputStyle } from './common';

const T = { primary: '#1565c0', primaryDark: '#0d47a1', accent: '#1565c0', cardBg: '#ffffff', border: '#dee2e6', th: '#1565c0' };
const cardStyle = { borderRadius: '12px', background: T.cardBg, border: `1px solid ${T.border}` };
const today = () => new Date().toISOString().split('T')[0];

function StockBadge({ status }) {
  if (status === 'empty') return <span className="badge bg-danger">Épuisé</span>;
  if (status === 'low') return <span className="badge bg-warning text-dark">Critique</span>;
  if (status === 'medium') return <span className="badge bg-info">Moyen</span>;
  return <span className="badge bg-success">OK</span>;
}

function ProgressBar({ percentage, status }) {
  const color = { empty: 'bg-danger', low: 'bg-warning', medium: 'bg-info' }[status] || 'bg-success';
  return (
    <div className="progress" style={{ height: '8px', backgroundColor: '#eef1f5' }}>
      <div className={`progress-bar ${color}`} role="progressbar" style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}></div>
    </div>
  );
}

export default function Stocks() {
  const { user } = useAuth();
  const [stocksData, setStocksData] = useState(null);
  const [cities, setCities] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({ city_id: '', pdv_id: '' });
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  const [restock, setRestock] = useState(null);   // { session_id, prize_config_id, prize_label, pdv_name, remaining_quantity }
  const [restockQty, setRestockQty] = useState(0);
  const [restockDate, setRestockDate] = useState(today());
  const [editStock, setEditStock] = useState(null); // { session, stock, max }

  const loadStocks = useCallback(async () => {
    try {
      const params = {};
      if (filters.city_id) params.city_id = filters.city_id;
      if (filters.pdv_id) params.pdv_id = filters.pdv_id;
      const { data } = await api.get('/palet/admin/stocks', { params });
      setStocksData(data.sessions);
    } catch {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des stocks' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadDailyOverview = useCallback(async () => {
    setDailyLoading(true);
    try {
      setDailyData((await api.get('/palet/admin/stocks/daily-overview')).data);
    } catch {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement de la vue journalière' });
    } finally {
      setDailyLoading(false);
    }
  }, []);

  useEffect(() => { loadStocks(); }, [loadStocks]);
  useEffect(() => { loadDailyOverview(); }, [loadDailyOverview]);
  useEffect(() => { api.get('/admin/cities').then((r) => setCities(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get('/admin/pdv', { params: filters.city_id ? { city_id: filters.city_id } : {} }).then((r) => setPdvs(r.data)).catch(() => {});
  }, [filters.city_id]);

  const refresh = () => { loadStocks(); loadDailyOverview(); };

  const handleRestock = async () => {
    if (!restock || !restockQty) return;
    try {
      await api.post('/palet/admin/stocks/restock', {
        session_id: restock.session_id, prize_config_id: restock.prize_config_id,
        quantity_added: restockQty, restock_date: restockDate,
      });
      setMessage({ type: 'success', text: `${restockQty > 0 ? '+' : ''}${restockQty} ${restock.prize_label} enregistrés pour le ${restockDate}` });
      setRestock(null);
      refresh();
    } catch (e) {
      setMessage({ type: 'danger', text: errorMessage(e, 'Erreur lors du réapprovisionnement') });
    }
  };

  const handleSaveTotal = async () => {
    try {
      await api.post('/palet/admin/stocks/set-total', {
        session_id: editStock.session.session_id, prize_config_id: editStock.stock.prize_config_id, max_quantity: editStock.max,
      });
      setMessage({ type: 'success', text: 'Stock mis à jour avec succès' });
      setEditStock(null);
      refresh();
    } catch (e) {
      setMessage({ type: 'danger', text: errorMessage(e, 'Erreur lors de la mise à jour') });
    }
  };

  const filteredDailyPdvs = useMemo(() => {
    let list = dailyData?.pdvs || [];
    if (filters.city_id) {
      const city = cities.find((c) => String(c.id) === String(filters.city_id));
      if (city) list = list.filter((p) => p.city_name === city.name);
    }
    if (filters.pdv_id) list = list.filter((p) => String(p.pdv_id) === String(filters.pdv_id));
    return list;
  }, [dailyData, filters, cities]);

  const totalsByCity = useMemo(() => {
    const byCity = new Map();
    for (const s of stocksData || []) {
      const cur = byCity.get(s.city_name || 'N/A') || { city_name: s.city_name || 'N/A', sessions_count: 0, max_quantity: 0, used_quantity: 0, remaining_quantity: 0 };
      cur.sessions_count += 1;
      for (const st of s.stocks) {
        cur.max_quantity += st.max_quantity; cur.used_quantity += st.used_quantity; cur.remaining_quantity += st.remaining_quantity;
      }
      byCity.set(cur.city_name, cur);
    }
    return [...byCity.values()].sort((a, b) => a.city_name.localeCompare(b.city_name));
  }, [stocksData]);

  const grid = useMemo(() => {
    const meta = new Map();
    const g = {};
    for (const s of stocksData || []) {
      const city = s.city_name || 'N/A';
      g[city] = g[city] || {};
      for (const st of s.stocks) {
        if (!meta.has(st.prize_type)) meta.set(st.prize_type, { label: st.prize_label, color: st.color });
        const cell = g[city][st.prize_type] || { max: 0, remaining: 0 };
        cell.max += st.max_quantity; cell.remaining += st.remaining_quantity;
        g[city][st.prize_type] = cell;
      }
    }
    return {
      cities: Object.keys(g).sort(),
      prizes: [...meta.entries()].map(([type, info]) => ({ type, ...info })).sort((a, b) => a.label.localeCompare(b.label)),
      g,
    };
  }, [stocksData]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    if (totalsByCity.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(totalsByCity.map((r) => ({
        Ville: r.city_name, Sessions: r.sessions_count, 'Stock total': r.max_quantity, 'Gagnés': r.used_quantity, Restant: r.remaining_quantity,
      }))), 'Totaux par ville');
    }
    const detail = [];
    for (const s of stocksData || []) {
      for (const st of s.stocks) {
        detail.push({ Ville: s.city_name || '', PDV: s.pdv_name || '', Session: s.session_name || '', Lot: st.prize_label,
          'Stock initial': st.initial_quantity, 'Ajouts / retraits': st.added_quantity, 'Stock total': st.max_quantity,
          'Gagnés': st.used_quantity, 'Remis': st.delivered_quantity, 'Réservés': st.reserved_quantity, Restant: st.remaining_quantity, Statut: st.status });
      }
    }
    if (detail.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), 'Détail par session');
    if (dailyData?.days?.length) {
      const rows = [];
      for (const p of dailyData.pdvs) {
        for (const prize of p.prizes) {
          const row = { Ville: p.city_name || '', PDV: p.pdv_name || '', Lot: prize.prize_label, Initial: prize.initial_quantity };
          prize.days.forEach((c, i) => { row[`J${i + 1} (${c.date})`] = c.stock_start; });
          row.Restant = prize.remaining_quantity;
          rows.push(row);
        }
      }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Vue journalière');
    }
    XLSX.writeFile(wb, `stocks_palet_${today()}.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f4f6f9' }}>
        <div className="spinner-border" role="status" style={{ color: T.accent }}></div>
      </div>
    );
  }

  const filterBar = (
    <div className="card border-0 shadow mb-3" style={cardStyle}>
      <div className="card-body p-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label small mb-1" style={{ color: '#333', fontWeight: 600 }}><i className="fas fa-city me-1" style={{ color: T.accent }}></i>Ville</label>
            <select className="form-select form-select-sm" value={filters.city_id} onChange={(e) => setFilters({ city_id: e.target.value, pdv_id: '' })} style={inputStyle}>
              <option value="">Toutes les villes</option>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1" style={{ color: '#333', fontWeight: 600 }}><i className="fas fa-store me-1" style={{ color: T.accent }}></i>Point de Vente</label>
            <select className="form-select form-select-sm" value={filters.pdv_id} onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })} disabled={!filters.city_id} style={inputStyle}>
              <option value="">{filters.city_id ? 'Tous les PDVs' : "Sélectionner une ville d'abord"}</option>
              {pdvs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {(filters.city_id || filters.pdv_id) && (
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary w-100" onClick={() => setFilters({ city_id: '', pdv_id: '' })}><i className="fas fa-times me-1"></i>Réinitialiser</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      <PageNav user={user} title="Stocks" />
      <div className="container py-4">
        <PageAlert message={message} onClose={() => setMessage({ type: '', text: '' })} />

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a1a1a' }}><i className="fas fa-warehouse me-2" style={{ color: T.primary }}></i>Gestion des Stocks</h2>
            <p className="mb-0" style={{ color: '#555' }}>{stocksData?.length || 0} session(s) active(s)</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-success fw-bold" onClick={handleExportExcel} disabled={!stocksData?.length && !dailyData?.pdvs?.length}>
              <i className="fas fa-file-excel me-2"></i>Exporter Excel
            </button>
            <button className="btn btn-primary fw-bold" onClick={() => window.print()} style={{ background: T.primary, border: 'none' }}>
              <i className="fas fa-print me-2"></i>Imprimer
            </button>
          </div>
        </div>

        <div className="mb-4" style={{ borderBottom: `2px solid ${T.border}` }}>
          {[{ key: 'daily', icon: 'fas fa-calendar-alt', label: 'Vue journalière' }, { key: 'stocks', icon: 'fas fa-boxes', label: 'Stocks globaux' }].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontWeight: 'bold', color: activeTab === tab.key ? T.accent : '#aaa', borderBottom: activeTab === tab.key ? `3px solid ${T.accent}` : '3px solid transparent', fontSize: '14px' }}>
              <i className={`${tab.icon} me-2`}></i>{tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'stocks' && (
          <>
            {filterBar}
            {totalsByCity.length > 0 && (
              <div className="card border-0 shadow mb-4" style={cardStyle}>
                <div className="card-header p-3" style={{ background: 'rgba(66,165,245,0.1)', borderBottom: `1px solid ${T.border}` }}>
                  <h5 className="mb-0" style={{ color: '#1a1a1a' }}><i className="fas fa-city me-2" style={{ color: T.primary }}></i>Totaux par ville</h5>
                </div>
                <div className="card-body p-3">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead><tr>{['Ville', 'Sessions', 'Stock total', 'Gagnés', 'Restant', 'Progression'].map((c) => <th key={c} className={c !== 'Ville' ? 'text-center' : ''} style={{ color: T.th }}>{c}</th>)}</tr></thead>
                      <tbody>
                        {totalsByCity.map((r) => {
                          const pct = r.max_quantity > 0 ? (r.remaining_quantity / r.max_quantity) * 100 : 0;
                          const status = r.max_quantity > 0 && r.remaining_quantity === 0 ? 'empty' : pct < 20 ? 'low' : pct < 50 ? 'medium' : 'ok';
                          return (
                            <tr key={r.city_name}>
                              <td className="fw-semibold">{r.city_name}</td>
                              <td className="text-center">{r.sessions_count}</td>
                              <td className="text-center">{r.max_quantity}</td>
                              <td className="text-center">{r.used_quantity}</td>
                              <td className="text-center fw-bold" style={{ color: status === 'empty' ? '#dc3545' : status === 'low' ? '#e68a00' : '#28a745' }}>{r.remaining_quantity}</td>
                              <td className="text-center" style={{ minWidth: '150px' }}><ProgressBar percentage={Math.round(pct)} status={status} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {grid.cities.length > 0 && grid.prizes.length > 0 && (
              <div className="card border-0 shadow mb-4" style={cardStyle}>
                <div className="card-header p-3" style={{ background: 'rgba(66,165,245,0.1)', borderBottom: `1px solid ${T.border}` }}>
                  <h5 className="mb-0" style={{ color: '#1a1a1a' }}><i className="fas fa-table me-2" style={{ color: T.primary }}></i>Stock par ville et par lot</h5>
                  <small style={{ color: '#555' }}>Stock restant par ville pour chaque lot</small>
                </div>
                <div className="card-body p-3">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ minWidth: '600px' }}>
                      <thead>
                        <tr>
                          <th style={{ color: T.th, minWidth: '140px' }}>Ville</th>
                          {grid.prizes.map((p) => (
                            <th key={p.type} className="text-center" style={{ color: T.th, minWidth: '100px' }}>
                              <span style={{ width: '10px', height: '10px', backgroundColor: p.color, borderRadius: '2px', display: 'inline-block', marginRight: '4px' }}></span>
                              <span style={{ fontSize: '12px' }}>{p.label}</span>
                            </th>
                          ))}
                          <th className="text-center" style={{ color: T.th }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grid.cities.map((city) => {
                          let total = 0;
                          return (
                            <tr key={city}>
                              <td className="fw-semibold">{city}</td>
                              {grid.prizes.map((p) => {
                                const cell = grid.g[city]?.[p.type];
                                const rem = cell?.remaining ?? 0; const max = cell?.max ?? 0;
                                total += rem;
                                const color = max === 0 ? '#666' : rem === 0 ? '#dc3545' : rem / max < 0.2 ? '#e68a00' : '#28a745';
                                return <td key={p.type} className="text-center" style={{ color }}>{max === 0 ? '—' : <><strong>{rem}</strong><small className="d-block text-muted" style={{ fontSize: '10px' }}>/ {max}</small></>}</td>;
                              })}
                              <td className="text-center fw-bold" style={{ color: T.accent }}>{total}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {!stocksData?.length ? (
              <div className="card border-0 shadow text-center p-5" style={cardStyle}>
                <i className="fas fa-box-open fa-4x mb-3" style={{ opacity: 0.3, color: T.accent }}></i>
                <p className="mb-0" style={{ color: '#555' }}>Aucune session active trouvée</p>
              </div>
            ) : stocksData.map((s) => (
              <div key={s.session_id} className="card border-0 shadow mb-4 stock-session" style={cardStyle}>
                <div className="card-header p-3" style={{ background: 'rgba(66,165,245,0.1)', borderBottom: `1px solid ${T.border}` }}>
                  <h5 className="mb-1 fw-bold" style={{ color: T.accent }}><i className="fas fa-store me-2"></i>{s.pdv_name}</h5>
                  <small style={{ color: '#555' }}>
                    <i className="fas fa-map-marker-alt me-1"></i>{s.city_name}<span className="mx-2">•</span>
                    <i className="fas fa-clock me-1"></i>{s.session_name}
                  </small>
                </div>
                <div className="card-body p-3">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>{['Lot', 'Stock initial', 'Ajouts / retraits', 'Gagnés', 'Réservés', 'Restant', 'Statut', 'Progression', 'Action'].map((c, i) => (
                          <th key={c} className={i > 0 ? 'text-center' : ''} style={{ color: T.th }}>{c}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {s.stocks.map((st) => (
                          <tr key={st.prize_config_id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ width: '20px', height: '20px', backgroundColor: st.color, borderRadius: '4px', marginRight: '10px', flexShrink: 0 }}></div>
                                <div><div className="fw-semibold">{st.prize_label}</div><small className="text-muted">{st.prize_name}</small></div>
                              </div>
                            </td>
                            <td className="text-center">{st.initial_quantity}</td>
                            <td className="text-center fw-bold" style={{ color: st.added_quantity < 0 ? '#dc3545' : '#28a745' }}>{st.added_quantity > 0 ? `+${st.added_quantity}` : st.added_quantity}</td>
                            <td className="text-center">{st.used_quantity}</td>
                            <td className="text-center text-muted" title="Lots dans des cases pas encore jouées">{st.reserved_quantity}</td>
                            <td className="text-center fw-bold" style={{ color: st.status === 'empty' ? '#dc3545' : st.status === 'low' ? '#e68a00' : '#28a745' }}>
                              {st.remaining_quantity}
                              {st.mismatch && <i className="fas fa-triangle-exclamation ms-1 text-danger" title="Écart avec le stock attendu"></i>}
                            </td>
                            <td className="text-center"><StockBadge status={st.status} /></td>
                            <td className="text-center" style={{ minWidth: '150px' }}><ProgressBar percentage={st.percentage} status={st.status} /></td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-1">
                                <button className="btn btn-sm btn-success" title="Ajouter du stock"
                                  onClick={() => { setRestock({ ...st, session_id: s.session_id, pdv_name: s.pdv_name }); setRestockQty(0); setRestockDate(today()); }}>
                                  <i className="fas fa-plus"></i>
                                </button>
                                <button className="btn btn-sm btn-primary" title="Modifier le stock total" style={{ background: T.primary, border: 'none' }}
                                  onClick={() => setEditStock({ session: s, stock: st, max: st.max_quantity })}>
                                  <i className="fas fa-edit"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'daily' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" style={{ color: '#1a1a1a' }}><i className="fas fa-store me-2" style={{ color: T.accent }}></i>Stocks par Point de Vente</h5>
              <button className="btn btn-sm btn-outline-secondary" onClick={loadDailyOverview}><i className="fas fa-sync-alt me-1"></i>Actualiser</button>
            </div>
            {filterBar}
            {dailyLoading ? (
              <div className="text-center py-5"><div className="spinner-border" role="status" style={{ color: T.accent }}></div></div>
            ) : filteredDailyPdvs.length === 0 ? (
              <div className="text-center py-5" style={{ color: '#555' }}>
                {dailyData?.pdvs?.length ? 'Aucun point de vente ne correspond aux filtres' : 'Aucune donnée disponible'}
              </div>
            ) : (
              <div>
                {filteredDailyPdvs.map((pdv) => {
                  const empty = pdv.prizes.some((p) => p.remaining_quantity === 0 && p.initial_quantity > 0);
                  const low = pdv.prizes.some((p) => p.remaining_quantity <= 5 && p.remaining_quantity > 0);
                  const tryAgain = pdv.try_again_by_day || {};
                  const tryTotal = Object.values(tryAgain).reduce((a, b) => a + b, 0);
                  return (
                    <div key={pdv.pdv_id} className="card border-0 shadow mb-4" style={{ ...cardStyle, border: `1px solid ${empty || low ? 'rgba(255,165,0,0.5)' : T.border}` }}>
                      <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'rgba(66,165,245,0.15)', borderBottom: `1px solid ${T.border}`, borderRadius: '12px 12px 0 0' }}>
                        <div>
                          <span className="fw-bold" style={{ fontSize: '15px', color: '#1a1a1a' }}><i className="fas fa-store me-2" style={{ color: T.accent }}></i>{pdv.pdv_name}</span>
                          {pdv.city_name && <span className="ms-2" style={{ fontSize: '12px', color: '#555' }}>{pdv.city_name}</span>}
                        </div>
                        <div className="d-flex gap-2">
                          {empty && <span className="badge" style={{ background: '#ff4444' }}><i className="fas fa-times-circle me-1"></i>Épuisé</span>}
                          {low && <span className="badge" style={{ background: '#ff8800' }}><i className="fas fa-exclamation-triangle me-1"></i>Stock faible</span>}
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div style={{ overflowX: 'auto' }}>
                          <table className="table mb-0">
                            <thead>
                              <tr style={{ background: '#f4f6f9' }}>
                                <th style={{ fontSize: '12px', minWidth: '130px', paddingLeft: '16px', color: '#555', position: 'sticky', left: 0, zIndex: 2, background: '#f4f6f9' }}>Lot</th>
                                <th className="text-center" style={{ fontSize: '12px', minWidth: '70px', color: '#555' }}>Initial</th>
                                {dailyData.days.map((day, i) => (
                                  <th key={day} className="text-center" style={{ fontSize: '11px', minWidth: '90px', color: '#555' }}>
                                    J{i + 1} · {new Date(`${day}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                  </th>
                                ))}
                                <th className="text-center fw-bold" style={{ fontSize: '12px', minWidth: '80px', color: '#1a1a1a' }}>Restant</th>
                                <th className="text-center" style={{ fontSize: '12px', minWidth: '90px', color: '#555' }}>Réappro</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pdv.prizes.map((prize) => {
                                const rem = prize.remaining_quantity;
                                const remAlert = rem <= 5 && rem > 0;
                                const remEmpty = rem === 0;
                                return (
                                  <tr key={`${prize.session_id}-${prize.prize_config_id}`}>
                                    <td style={{ paddingLeft: '16px', position: 'sticky', left: 0, zIndex: 1, background: '#ffffff' }}>
                                      <div className="d-flex align-items-center gap-2">
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: prize.color, display: 'inline-block', flexShrink: 0 }}></span>
                                        <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{prize.prize_label}</span>
                                      </div>
                                    </td>
                                    <td className="text-center" style={{ fontSize: '13px', color: '#555' }}>{prize.initial_quantity}</td>
                                    {prize.days.map((cell) => {
                                      const a = cell.stock_start <= 5 && cell.stock_start > 0;
                                      const e = cell.stock_start <= 0;
                                      return (
                                        <td key={cell.date} className="text-center" style={{ background: e ? 'rgba(66,165,245,0.1)' : a ? 'rgba(255,165,0,0.08)' : 'transparent' }}>
                                          <span style={{ fontWeight: 'bold', fontSize: '13px', color: e ? '#42a5f5' : a ? '#ffa500' : '#1a1a1a' }}>
                                            {e && <i className="fas fa-times-circle me-1" style={{ fontSize: '10px' }}></i>}
                                            {a && !e && <i className="fas fa-exclamation-triangle me-1" style={{ fontSize: '10px' }}></i>}
                                            {cell.stock_start}
                                          </span>
                                          {cell.wins > 0 && <div style={{ fontSize: '9px', color: '#999' }}>−{cell.wins}</div>}
                                          {cell.restock !== 0 && <div style={{ fontSize: '9px', color: cell.restock > 0 ? '#28a745' : '#dc3545' }}>{cell.restock > 0 ? '+' : ''}{cell.restock}</div>}
                                        </td>
                                      );
                                    })}
                                    <td className="text-center" style={{ background: remEmpty ? 'rgba(66,165,245,0.15)' : remAlert ? 'rgba(255,165,0,0.1)' : 'transparent' }}>
                                      <span style={{ fontWeight: 'bold', fontSize: '15px', color: remEmpty ? '#42a5f5' : remAlert ? '#ffa500' : '#28a745' }}>
                                        {remEmpty && <i className="fas fa-times-circle me-1"></i>}{remAlert && <i className="fas fa-exclamation-triangle me-1"></i>}{rem}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <button className="btn btn-sm" style={{ background: `linear-gradient(135deg,${T.primary},${T.primaryDark})`, color: '#fff', border: 'none', fontSize: '11px', padding: '3px 8px' }}
                                        onClick={() => { setRestock({ ...prize, pdv_name: pdv.pdv_name }); setRestockQty(0); setRestockDate(today()); }}>
                                        <i className="fas fa-plus me-1"></i>Réappro
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr style={{ borderTop: '1px dashed #ffd0d0', background: '#fff8f8' }}>
                                <td style={{ paddingLeft: '16px', position: 'sticky', left: 0, zIndex: 1, background: '#fff8f8' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#e57373', display: 'inline-block' }}></span>
                                    <span style={{ fontSize: '13px', color: '#c62828', fontWeight: 600 }}>Sans lot</span>
                                  </div>
                                </td>
                                <td className="text-center" style={{ fontSize: '13px', color: '#bbb' }}>—</td>
                                {dailyData.days.map((day) => {
                                  const n = tryAgain[day] ?? 0;
                                  return <td key={day} className="text-center"><span style={{ fontWeight: n ? 'bold' : 'normal', fontSize: '13px', color: n ? '#c62828' : '#ccc' }}>{n || '—'}</span></td>;
                                })}
                                <td className="text-center"><span style={{ fontWeight: 'bold', fontSize: '15px', color: tryTotal ? '#c62828' : '#ccc' }}>{tryTotal || '—'}</span></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <p className="small mt-1" style={{ color: '#555' }}>
                  Stock en début de journée · <span style={{ color: '#999' }}>−N</span> lots gagnés · <span style={{ color: '#28a745' }}>+N</span> réappro ·
                  <i className="fas fa-exclamation-triangle ms-2 me-1" style={{ color: '#ffa500' }}></i>Alerte &lt; 5 —
                  <i className="fas fa-times-circle ms-2 me-1" style={{ color: T.accent }}></i>Épuisé
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {restock && (
        <Modal title="Réapprovisionnement" icon="fas fa-plus-circle" onClose={() => setRestock(null)}
          footer={<>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRestock(null)}>Annuler</button>
            <button className="btn btn-sm fw-bold" style={{ background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`, color: '#fff', border: 'none' }}
              disabled={!restockQty || restock.remaining_quantity + restockQty < 0} onClick={handleRestock}>
              <i className="fas fa-check me-2"></i>Confirmer {restockQty > 0 ? '+' : ''}{restockQty}
            </button>
          </>}>
          <p className="mb-1" style={{ color: '#555' }}>PDV : <strong style={{ color: '#1a1a1a' }}>{restock.pdv_name}</strong></p>
          <p className="mb-3" style={{ color: '#555' }}>Lot : <strong style={{ color: '#1a1a1a' }}>{restock.prize_label}</strong></p>
          <div className="mb-3">
            <label className="form-label small" style={{ color: '#333' }}>Date du réapprovisionnement</label>
            <input type="date" className="form-control" value={restockDate} onChange={(e) => setRestockDate(e.target.value)} style={inputStyle} />
          </div>
          <div className="mb-3">
            <label className="form-label small" style={{ color: '#333' }}>Quantité à ajouter (négatif pour une casse / un retrait)</label>
            <div className="input-group">
              <button className="btn btn-outline-secondary" onClick={() => setRestockQty((q) => q - 1)}><i className="fas fa-minus"></i></button>
              <input type="number" className="form-control text-center fw-bold" value={restockQty} onChange={(e) => setRestockQty(parseInt(e.target.value, 10) || 0)} style={{ ...inputStyle, fontSize: '20px' }} />
              <button className="btn btn-outline-secondary" onClick={() => setRestockQty((q) => q + 1)}><i className="fas fa-plus"></i></button>
            </div>
          </div>
          <div className="p-2 rounded" style={{ background: 'rgba(66,165,245,0.1)', border: `1px solid ${T.border}` }}>
            <small style={{ color: '#555' }}>
              Stock actuel : <strong style={{ color: '#1a1a1a' }}>{restock.remaining_quantity}</strong> →
              Après réappro : <strong style={{ color: '#1a1a1a' }}>{restock.remaining_quantity + restockQty}</strong>
            </small>
          </div>
        </Modal>
      )}

      {editStock && (
        <Modal title="Modifier le stock total" icon="fas fa-edit" onClose={() => setEditStock(null)}
          footer={<>
            <button type="button" className="btn btn-secondary" onClick={() => setEditStock(null)}>Annuler</button>
            <button type="button" className="btn btn-primary" onClick={handleSaveTotal} style={{ background: T.primary, border: 'none' }}
              disabled={editStock.max < editStock.stock.max_quantity - editStock.stock.remaining_quantity}>
              <i className="fas fa-save me-2"></i>Enregistrer
            </button>
          </>}>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Point de Vente</label><input className="form-control" value={editStock.session.pdv_name} disabled style={inputStyle} /></div>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Lot</label><input className="form-control" value={editStock.stock.prize_label} disabled style={inputStyle} /></div>
          <div className="mb-3">
            <label className="form-label" style={{ color: '#333' }}>Stock total de la session</label>
            <input type="number" min="0" className="form-control" value={editStock.max} onChange={(e) => setEditStock({ ...editStock, max: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
            <small className="text-muted">Gagnés + réservés : {editStock.stock.max_quantity - editStock.stock.remaining_quantity} (minimum possible)</small>
          </div>
          <div className="alert alert-info" style={{ background: 'rgba(66,165,245,0.1)', borderColor: 'rgba(66,165,245,0.3)' }}>
            <small style={{ color: '#555' }}><i className="fas fa-info-circle me-2"></i>Stock restant après modification : <strong style={{ color: '#1a1a1a' }}>
              {Math.max(0, editStock.stock.remaining_quantity + editStock.max - editStock.stock.max_quantity)}</strong> · la modification est tracée dans le journal.</small>
          </div>
        </Modal>
      )}
    </div>
  );
}
